// frontend/shared/src/page.ts —— spec §2 page：用户端 has_more 与后台 / 代理 adminlist 两种分页协议（TD-820 / TD-837）
import type { UserPage } from './api/types';

export interface ListQuery {
  page: number;
  size: number;
  keyword?: string;
  facets?: Readonly<Record<string, string>>;
  start?: string;
  end?: string;
  tab?: string;
  sort?: string;
  order?: 'asc' | 'desc';
}

// 02 §2.2 列表参数（与原型 mkReq 同序）；空串条件不下发
export function listQuery(q: ListQuery): Record<string, string | number> {
  const out: Record<string, string | number> = { page: q.page, size: q.size };
  if (q.keyword) out.keyword = q.keyword;
  for (const [k, v] of Object.entries(q.facets ?? {})) if (v) out[k] = v;
  if (q.start) out.start_date = q.start;
  if (q.end) out.end_date = q.end;
  if (q.tab) out.tab = q.tab;
  if (q.sort) {
    out.sort = q.sort;
    out.order = q.order ?? 'desc';
  }
  return out;
}

export function reqLine(endpoint: string, q: ListQuery): string {
  const qs = Object.entries(listQuery(q)).map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`);
  return `GET ${endpoint}?${qs.join('&')}`;
}

// 原型 winStart 公式：最多 5 个页码按钮，当前页尽量居中
export function pageWindow(pageNo: number, totalPages: number): number[] {
  const start = Math.max(1, Math.min(pageNo - 2, totalPages - 4));
  const out: number[] = [];
  for (let i = start; i <= Math.min(totalPages, start + 4); i++) out.push(i);
  return out;
}

// TD-837：用户端「加载更多」列表。并发 loadMore 共用同一请求；loadFirst 递增代号，
// 丢弃旧代号仍在途请求的结果（切 tab / 下拉刷新时避免旧页拼进新列表）。
export class CursorList<T> {
  items: T[] = [];
  page = 0;
  hasMore = true;
  firstLoading = false;
  moreLoading = false;
  error = false;
  private inflight: Promise<void> | null = null;
  private gen = 0;

  constructor(private readonly fetcher: (page: number) => Promise<UserPage<T>>) {}

  loadFirst(): Promise<void> {
    this.gen++;
    this.items = [];
    this.page = 0;
    this.hasMore = true;
    this.firstLoading = true;
    this.inflight = null;
    return this.loadMore();
  }

  loadMore(): Promise<void> {
    if (this.inflight) return this.inflight;
    if (!this.hasMore) return Promise.resolve();
    const gen = this.gen;
    const next = this.page + 1;
    if (next > 1) this.moreLoading = true;
    this.error = false;
    const p: Promise<void> = this.fetcher(next)
      .then((r) => {
        if (gen !== this.gen) return;
        this.items = this.items.concat(r.list);
        this.page = next;
        this.hasMore = r.has_more;
      })
      .catch((e: unknown) => {
        if (gen !== this.gen) return;
        console.error('[gp:page]', next, e);
        this.error = true;
      })
      .finally(() => {
        if (gen !== this.gen || this.inflight !== p) return;
        this.firstLoading = false;
        this.moreLoading = false;
        this.inflight = null;
      });
    this.inflight = p;
    return p;
  }
}
